/* Shared lead detail drawer + the small presentational helpers it needs.
   Used by leadIQ (full inbox) and campaignIQ (the "who" behind a campaign). */
import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Avatar, Badge, Button, IconButton } from '@/components/ui';
import { deleteLead, qk } from '@/data/client';
import { Icon } from '@/lib/icons';
import { fmtDateTime } from '@/lib/leadTime';
import { useIsAdmin } from '@/auth/useIsAdmin';
import { LEAD_STATUSES, STATUS_META, type LeadStatus } from '@/data/status';
import type { Channel, Lead, LeadSource } from '@/data/types';

// A stored call-audio link only plays inline if it's a direct media file. Call-
// tracking providers (e.g. Nimbata) often store a login-gated dashboard *page*
// URL instead, which a native <audio> element can't play — so we treat only real
// media URLs as playable and offer the rest as an external link (admins only).
const AUDIO_EXT = /\.(mp3|wav|m4a|aac|ogg|oga|flac|mpeg|mp4)(\?|#|$)/i;
function isDirectAudioUrl(url: string): boolean {
  try { return AUDIO_EXT.test(new URL(url).pathname); } catch { return AUDIO_EXT.test(url); }
}
function recordingLinkLabel(url: string): string {
  return /nimbata\./i.test(url) ? 'Open recording in Nimbata' : 'Open call recording';
}

const CHANNEL_META: Record<Channel, { label: string; icon: string; color: string }> = {
  email: { label: 'Email', icon: 'mail', color: 'var(--triton-blue)' },
  chat: { label: 'Chat', icon: 'message', color: 'var(--dark-700)' },
  phone: { label: 'Phone Call', icon: 'phone', color: 'var(--slate-500)' },
  sms: { label: 'SMS', icon: 'zap', color: 'var(--cream-400)' },
  referral: { label: 'Referral', icon: 'users', color: 'var(--green-600)' },
};
export function channelMeta(c: Channel | null) {
  return (c && CHANNEL_META[c]) || { label: '—', icon: 'message', color: 'var(--slate-400)' };
}

type SourceVariant = 'neutral' | 'accent' | 'success';
const SOURCE_META: Record<LeadSource, { label: string; paid: boolean; variant: SourceVariant }> = {
  organic: { label: 'Organic', paid: false, variant: 'neutral' },
  paid: { label: 'Paid', paid: true, variant: 'accent' },
  referral: { label: 'Organic', paid: false, variant: 'neutral' },
  ai: { label: 'AI', paid: false, variant: 'success' },
};
export function sourceMeta(s: LeadSource | null): { label: string; paid: boolean; variant: SourceVariant } {
  return (s && SOURCE_META[s]) || { label: '—', paid: false, variant: 'neutral' };
}
/** Referral is treated as Organic in the UI (both are non-paid); AI stays distinct. */
export function normSource(s: LeadSource | null): LeadSource | null {
  return s === 'referral' ? 'organic' : s;
}

const SCORE_COLORS: Record<number, string> = {
  5: 'var(--triton-blue)', 4: 'var(--triton-blue)', 3: 'var(--slate-500)',
  2: 'var(--cream-400)', 1: 'var(--slate-400)',
};

const STATUS_COLOR: Record<string, string> = {
  neutral: 'var(--text-muted)', accent: 'var(--blue-700)', warning: 'var(--amber-700)',
  success: 'var(--green-700)', outline: 'var(--text-subtle)',
};

export function fmtMoney(n: number | null): string {
  return n ? '$' + n.toLocaleString('en-US') : '—';
}
export function fmtWhen(days: number): string {
  if (days >= 9999) return '—';
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 30) return `${days}d ago`;
  return `${Math.round(days / 7)}w ago`;
}
export { fmtDateTime } from '@/lib/leadTime';
/** Show just the domain + path of a referrer URL, trimmed. */
export function fmtReferrer(url: string | null | undefined): string {
  if (!url) return '';
  return url.replace(/^https?:\/\//, '').replace(/\/$/, '');
}

export function StatusSelect({ value, onChange }: { value: LeadStatus; onChange: (s: LeadStatus) => void }) {
  return (
    <select
      className="lead-status"
      value={value}
      style={{ color: STATUS_COLOR[STATUS_META[value].variant] }}
      onClick={(e) => e.stopPropagation()}
      onChange={(e) => onChange(e.target.value as LeadStatus)}
      aria-label="Lead status"
    >
      {LEAD_STATUSES.map((s) => (
        <option key={s.value} value={s.value}>{s.label}</option>
      ))}
    </select>
  );
}

export function ScoreDots({ score }: { score: number }) {
  const color = SCORE_COLORS[score] ?? 'var(--slate-400)';
  return (
    <span className="lead-score" title={`AI quality score: ${score || '—'} of 5`}>
      <span className="lead-score__num" style={{ color }}>{score || '–'}</span>
      <span className="lead-score__dots">
        {[1, 2, 3, 4, 5].map((i) => (
          <i key={i} className="lead-score__dot" style={{ background: i <= score ? color : 'var(--border-strong)' }} />
        ))}
      </span>
    </span>
  );
}

export function ChannelBadge({ channel }: { channel: Channel | null }) {
  const m = channelMeta(channel);
  return (
    <span className="lead-chanbadge">
      <span className="lead-chanbadge__icon" style={{ color: m.color }}><Icon name={m.icon} size={15} /></span>
      {m.label}
    </span>
  );
}

export function LeadDrawer({
  lead, onClose, onSetStatus, onSaveValue,
}: {
  lead: Lead | null;
  onClose: () => void;
  onSetStatus: (uid: string, status: LeadStatus) => void;
  onSaveValue: (uid: string, value: number | null) => void;
}) {
  const [tab, setTab] = useState<'summary' | 'raw'>('summary');
  const isAdmin = useIsAdmin();
  const qc = useQueryClient();
  const [deleting, setDeleting] = useState(false);
  const [delErr, setDelErr] = useState('');
  /* Admin only, with a typed confirmation: a wrong-table sync can land one
     lead on the wrong client, and the fix must not be a click away from the
     status dropdown. Everything that counts leads refetches afterwards. */
  const remove = async () => {
    if (!lead) return;
    if (!window.confirm(`Delete "${lead.name}" from this client for good? It will not come back on the next sync.`)) return;
    setDeleting(true); setDelErr('');
    try {
      await deleteLead(lead.uid);
      for (const k of [qk.leads, qk.kpis, qk.leadSources, qk.performance]) qc.invalidateQueries({ queryKey: k });
      onClose();
    } catch (e) { setDelErr(e instanceof Error ? e.message : String(e)); }
    finally { setDeleting(false); }
  };
  useEffect(() => { setTab('summary'); }, [lead?.uid]);
  if (!lead) return null;
  const m = channelMeta(lead.channel);
  const rawLabel = lead.channel === 'email' ? 'Full email' : lead.channel === 'phone' ? 'Call transcript' : 'Full transcript';
  const commitValue = (raw: string) => {
    const trimmed = raw.trim();
    const next = trimmed === '' ? null : Number(trimmed);
    if (next === null || !Number.isNaN(next)) onSaveValue(lead.uid, next);
  };
  return (
    <div className="lead-drawer" role="dialog" aria-modal="true">
      <div className="lead-drawer__scrim" onClick={onClose} />
      <div className="lead-drawer__panel">
        <div className="lead-drawer__top">
          <div className="lead-drawer__id">
            <Avatar name={lead.name} tone="cream" />
            <div>
              <strong>{lead.name}</strong>
              <span><ChannelBadge channel={lead.channel} /></span>
            </div>
          </div>
          <IconButton label="Close" variant="ghost" onClick={onClose}><Icon name="x" size={18} /></IconButton>
        </div>

        {(lead.email || lead.phone || lead.company) && (
          <div className="lead-drawer__contact">
            {lead.company && <span>{lead.company}</span>}
            {lead.phone && <span>{lead.phone}</span>}
            {lead.email && <span>{lead.email}</span>}
          </div>
        )}

        <div className="lead-drawer__meta">
          <div><em>AI quality</em><ScoreDots score={lead.score} /></div>
          <div><em>Source</em><Badge variant={sourceMeta(normSource(lead.source)).variant}>{sourceMeta(normSource(lead.source)).label}</Badge></div>
          <div>
            <em>Est. value</em>
            <div className="tr-field__control" style={{ height: 36, maxWidth: 160 }}>
              <span style={{ color: 'var(--text-subtle)' }}>$</span>
              <input
                key={lead.uid}
                className="tr-input"
                type="number"
                inputMode="decimal"
                placeholder="Add value"
                defaultValue={lead.value ?? ''}
                onBlur={(e) => commitValue(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
              />
            </div>
          </div>
          <div style={{ gridColumn: '1 / -1' }}><em>Received</em><span>{fmtDateTime(lead.receivedAt)}</span></div>
          {lead.referrer && (
            <div style={{ gridColumn: '1 / -1' }}>
              <em>Referring page</em>
              <a className="lead-drawer__referrer" href={lead.referrer} target="_blank" rel="noreferrer" title={lead.referrer}>{fmtReferrer(lead.referrer)}</a>
            </div>
          )}
          {lead.campaign && (
            <div style={{ gridColumn: '1 / -1' }}>
              <em>Campaign</em>
              <span>{lead.campaign}</span>
            </div>
          )}
          {lead.referral && (
            <div style={{ gridColumn: '1 / -1' }}>
              <em>Referred by</em>
              <span>
                <strong>{lead.referral.referredByClient || 'a client'}</strong>{lead.referral.referredByUser ? ` · ${lead.referral.referredByUser}` : ''}
                {' · '}<Badge variant={lead.referral.status === 'qualified' ? 'success' : lead.referral.status === 'not_fit' || lead.referral.status === 'existing_client' ? 'outline' : 'neutral'} dot={lead.referral.status === 'qualified'}>{lead.referral.statusLabel}</Badge>
                {lead.referral.status === 'qualified' && lead.referral.creditAmount != null && <span className="dash-muted"> · ${lead.referral.creditAmount} credit{lead.referral.creditIssuedOn ? ` issued ${lead.referral.creditIssuedOn}` : ' not yet issued'}</span>}
              </span>
              {isAdmin && <span className="dash-muted" style={{ fontSize: 'var(--text-xs)' }}>Work it under Admin → Referral Rewards: contacted, qualified (earns the credit), and &ldquo;credit issued on&rdquo;.</span>}
            </div>
          )}
        </div>

        <div className="lead-drawer__tabs">
          <button className={tab === 'summary' ? 'is-active' : ''} onClick={() => setTab('summary')}>
            <Icon name="sparkles" size={15} /> AI summary
          </button>
          <button className={tab === 'raw' ? 'is-active' : ''} onClick={() => setTab('raw')}>
            <Icon name={m.icon} size={15} /> {rawLabel}
          </button>
        </div>
        <div className="lead-drawer__content">
          {tab === 'summary' ? (
            <div className="lead-drawer__ai">
              <span className="lead-drawer__aimark"><Icon name="sparkles" size={16} /></span>
              <p>{lead.summary || 'No AI summary available.'}</p>
            </div>
          ) : (
            <>
              {lead.callAudioLink && (
                isDirectAudioUrl(lead.callAudioLink) ? (
                  <audio controls src={lead.callAudioLink} style={{ width: '100%', marginBottom: 14 }} />
                ) : isAdmin ? (
                  <a className="lead-drawer__recording" href={lead.callAudioLink} target="_blank" rel="noreferrer">
                    <Icon name="play" size={15} />
                    {recordingLinkLabel(lead.callAudioLink)}
                    <Icon name="external" size={13} />
                  </a>
                ) : null
              )}
              <pre className="lead-drawer__raw">{lead.transcript || 'No transcript available.'}</pre>
            </>
          )}
        </div>
        <div className="lead-drawer__foot">
          <label className="lead-drawer__status">
            <span>Status</span>
            <StatusSelect value={lead.status} onChange={(s) => onSetStatus(lead.uid, s)} />
          </label>
          {isAdmin && (
            <Button variant="ghost" size="sm" disabled={deleting} title="Admin only — removes the lead and keeps it from re-syncing" onClick={remove} iconLeft={<Icon name="trash" size={14} />}>
              {deleting ? 'Deleting…' : 'Delete'}
            </Button>
          )}
          <Button variant="primary" size="sm" iconRight={<Icon name="arrow-right" size={15} />}>Open in CRM</Button>
        </div>
        {delErr && <p className="admin-modal__err" style={{ padding: '0 16px 12px' }}>{delErr}</p>}
      </div>
    </div>
  );
}
